/**
 * @file
 * Font Awesome SVG core: replaces every Font Awesome <i> element (and every
 * Font Awesome CSS pseudo-element) on the page with an inline <svg>.
 *
 * Ported from source/default/_patterns/01-atoms/svg/fontawesome.js
 * (MCFSFT-4958). Same Font Awesome config, same icon set, same dom.watch()
 * call -- only the bundle it runs from changed (sdc.js instead of app.js).
 * The PL copy no longer runs on Drupal pages: its call moved into the PL
 * atom's enable(), which Drupal skips via MIGRATED_TO_SDC.
 *
 * This is a page-wide service, not per-component markup: the <i> elements
 * it converts are hand-written by many templates (the social menus,
 * social-media-links, pager, hero, event-card, the FAQ search button) and
 * the pseudo-elements come from pager/datatable/table CSS. None of them
 * render `particle:svg`.
 *
 * Deliberately NOT named svg.js: Drupal SDC auto-attaches
 * [component].js from the component directory as a raw, unbundled file
 * whenever the component renders, and the `import` statements below would
 * throw a SyntaxError there. This file is only ever loaded through the
 * webpack SDC bundle (components/index.js).
 */

import { config, library, dom } from '@fortawesome/fontawesome-svg-core';

// Import specific icons required (same order as the PL copy, for diffing).

import { faFacebookF } from '@fortawesome/free-brands-svg-icons/faFacebookF';
import { faInstagram } from '@fortawesome/free-brands-svg-icons/faInstagram';

import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { faSort } from '@fortawesome/free-solid-svg-icons/faSort';
import { faSortDown } from '@fortawesome/free-solid-svg-icons/faSortDown';
import { faSortUp } from '@fortawesome/free-solid-svg-icons/faSortUp';
import { faPlayCircle } from '@fortawesome/free-solid-svg-icons/faPlayCircle';
import { faTwitter } from '@fortawesome/free-brands-svg-icons/faTwitter';
import { faFlickr } from '@fortawesome/free-brands-svg-icons/faFlickr';
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons/faLinkedin';
import { faTiktok } from '@fortawesome/free-brands-svg-icons/faTiktok';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons/faXTwitter';

// Convert CSS pseudo-elements that set a Font Awesome font-family/content
// (the datatable, pager and table sort/arrow icons). The PL copy said this
// was "Needed for mobile submenu" -- stale since at least MCFSFT-4958:
// no menu/header/megamenu CSS uses Font Awesome pseudo-elements any more.
config.searchPseudoElements = true;

// Only icons added here render; any other fa-* icon is drawn as Font
// Awesome's "missing icon" placeholder.
library.add(
  faArrowRight,
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faFacebookF,
  faInstagram,
  faSearch,
  faSort,
  faSortDown,
  faSortUp,
  faPlayCircle,
  faTwitter,
  faFlickr,
  faYoutube,
  faLinkedin,
  faTiktok,
  faEnvelope,
  faXTwitter
);

((Drupal) => {
  // eslint-disable-next-line no-param-reassign
  Drupal.behaviors.svgFontAwesome = {
    attach() {
      // dom.watch() converts the whole document once, then keeps a
      // MutationObserver on it, so markup that Drupal inserts later (AJAX,
      // Layout Builder, Vue re-renders) is converted without any
      // per-`context` work here. It must only start once: every extra call
      // adds another observer.
      const root = document.documentElement;
      if (root.dataset.svgFontawesomeInitialized) {
        return;
      }
      root.dataset.svgFontawesomeInitialized = 'true';
      dom.watch();
    },

    detach(context, settings, trigger) {
      // The observer is page-wide, so only stop it when the whole document
      // is unloaded -- never for a detached fragment.
      if (trigger === 'unload' && context === document) {
        dom.unwatch();
        delete document.documentElement.dataset.svgFontawesomeInitialized;
      }
    },
  };
})(Drupal);
